import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createElementVNode as _createElementVNode, unref as _unref, createBlock as _createBlock, createVNode as _createVNode, Transition as _Transition, withCtx as _withCtx, createTextVNode as _createTextVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-4cc28dca"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["id"]
const _hoisted_2 = ["for", "data-invalid", "data-disabled"]
const _hoisted_3 = { class: "bar-text-field__container" }
const _hoisted_4 = {
  key: 0,
  class: "bar-text-field__box"
}
const _hoisted_5 = ["id", "type", "placeholder", "aria-describedby", "value", "required", "disabled", "maxlength", "step", "min", "max"]
const _hoisted_6 = ["aria-label", "disabled", "data-invalid"]
const _hoisted_7 = ["aria-label", "data-invalid"]
const _hoisted_8 = {
  key: 0,
  role: "tooltip",
  class: "bar-text-field__tooltip"
}
const _hoisted_9 = {
  key: 4,
  class: "bar-text-field__box"
}
const _hoisted_10 = ["id"]

import { PropType, computed, unref, ref, useSlots } from 'vue'
import EyeIcon from '@/icons/EyeIcon.vue'
import EyeSlashIcon from '@/icons/EyeSlashIcon.vue'
import XLgIcon from '@/icons/XLgIcon.vue'
import type { Validation } from '@vuelidate/core'
import { createAutoIncrementIdProp } from '@/utils/props'
import locales from './BarTextField.locales.en.json'


export default /*#__PURE__*/_defineComponent({
  __name: 'BarTextField',
  props: {
  id: createAutoIncrementIdProp('bar-text-field'),
  modelValue: {
    type: [String, Number],
    default: () => ''
  },
  label: {
    type: String,
    default: () => ''
  },
  labelClass: {
    type: String,
    default: () => ''
  },
  type: {
    type: String,
    default: () => 'text'
  },
  placeholder: {
    type: String,
    default: () => ''
  },
  disabled: Boolean,
  validation: {
    type: Object as PropType<Validation>,
    default: () => undefined
  },
  prepend: {
    type: String,
    default: () => ''
  },
  append: {
    type: String,
    default: () => ''
  },
  debounce: {
    type: Number,
    default: 0
  },
  maxlength: {
    type: [String, Number],
    default: ''
  },
  autoTrim: Boolean,
  togglePassword: Boolean,
  step: {
    type: Number,
    default: () => undefined
  },
  min: {
    type: Number,
    default: () => undefined
  },
  max: {
    type: Number,
    default: () => undefined
  }
},
  emits: ['update:modelValue'],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit
const slots = useSlots()

const input = ref<HTMLInputElement>()
const showPassword = ref<boolean>(false)
const showSearch = computed<boolean>(() => props.type === 'text')
const focused = ref<boolean>(false)
const existTooltip = computed<boolean>(() => Boolean(slots.tooltip?.()))
const dismissTooltip = ref<boolean>(false)
const inputId = computed(() => props.id + '__input')
const invalidMessageId = computed(() => props.id + '__invalid_message')

const dirty = computed<boolean>(() => {
  return props.validation?.$dirty ?? false
})

const invalid = computed<boolean>(() => {
  return props.validation?.$invalid ?? false
})

const invalidMessage = computed<string>(() => {
  const message = props.validation?.$errors?.[0]?.$message ?? ''
  return unref(message)
})

const showRequiredAttr = computed<boolean>(() => {
  return props.validation
    ? Object.keys(props.validation).includes('required')
    : false
})

function handleChange(event: Event) {
  if (props.autoTrim) {
    const target = event.target as HTMLInputElement
    emit('update:modelValue', target.value.trim())
  }
}

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement
    emit('update:modelValue', target.value)
}

function handleClearButtonClick() {
  emit('update:modelValue', '')
  input.value?.focus()
}

function toggleShowPassword() {
  showPassword.value = !showPassword.value
}

function handleInputFocus() {
  focused.value = true
}

function handleInputBlur() {
  focused.value = false
  dismissTooltip.value = false
}

function handleInputKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape' || event.key === 'esc') {
    dismissTooltip.value = true
  }
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    id: __props.id,
    class: _normalizeClass(["bar-text-field", { 'bs-invalid-field': dirty.value && invalid.value }])
  }, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          for: inputId.value,
          class: _normalizeClass(['bar-text-field__label bs-field-label', __props.labelClass]),
          "data-invalid": dirty.value && invalid.value,
          "data-disabled": __props.disabled
        }, _toDisplayString(__props.label), 11, _hoisted_2))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _renderSlot(_ctx.$slots, "base-prepend"),
      (__props.prepend)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(__props.prepend), 1))
        : _createCommentVNode("", true),
      _createElementVNode("input", {
        id: inputId.value,
        ref_key: "input",
        ref: input,
        class: _normalizeClass(["bar-text-field__input", {
          'bar-text-field__input--invalid': dirty.value && invalid.value,
          'bar-text-field__input--search': showSearch.value && !__props.modelValue
        }]),
        type: showPassword.value ? 'text' : __props.type,
        placeholder: __props.placeholder,
        "aria-describedby": invalidMessage.value && invalidMessageId.value || undefined,
        value: __props.modelValue,
        required: showRequiredAttr.value,
        disabled: __props.disabled,
        maxlength: __props.maxlength,
        step: __props.step,
        min: __props.min,
        max: __props.max,
        onChange: handleChange,
        onInput: handleInput,
        onFocus: handleInputFocus,
        onBlur: handleInputBlur,
        onKeydown: handleInputKeyDown
      }, null, 42, _hoisted_5),
      (__props.type === 'password' && __props.togglePassword)
        ? (_openBlock(), _createElementBlock("button", {
            key: 1,
            class: _normalizeClass(["bar-text-field__button", dirty.value && invalid.value ? 'is-invalid' : '']),
            type: "button",
            "aria-label": 
          showPassword.value
            ? _unref(locales).aria_label_password_visible
            : _unref(locales).aria_label_password_hidden
        ,
            disabled: __props.disabled,
            "data-invalid": dirty.value && invalid.value,
            onClick: _cache[0] || (_cache[0] = ($event: any) => (toggleShowPassword()))
          }, [
            (showPassword.value)
              ? (_openBlock(), _createBlock(EyeSlashIcon, { key: 0 }))
              : (_openBlock(), _createBlock(EyeIcon, { key: 1 }))
          ], 10, _hoisted_6))
        : _createCommentVNode("", true),
      (showSearch.value && __props.modelValue && !__props.disabled)
        ? (_openBlock(), _createElementBlock("button", {
            key: 2,
            class: _normalizeClass(["bar-text-field__button", dirty.value && invalid.value ? 'is-invalid' : '']),
            type: "button",
            "aria-label": _unref(locales).clear_search_text,
            "data-invalid": dirty.value && invalid.value,
            onClick: handleClearButtonClick
          }, [
            _createVNode(XLgIcon)
          ], 10, _hoisted_7))
        : _createCommentVNode("", true),
      (existTooltip.value)
        ? (_openBlock(), _createBlock(_Transition, {
            key: 3,
            name: "fade"
          }, {
            default: _withCtx(() => [
              (focused.value && !dismissTooltip.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                    _renderSlot(_ctx.$slots, "tooltip")
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 3
          }))
        : _createCommentVNode("", true),
      (__props.append)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _renderSlot(_ctx.$slots, "append", {}, () => [
              _createTextVNode(_toDisplayString(__props.append), 1)
            ])
          ]))
        : _createCommentVNode("", true),
      _renderSlot(_ctx.$slots, "base-append"),
      (dirty.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 5,
            id: invalidMessageId.value,
            class: "bar-text-field__invalid-feedback"
          }, _toDisplayString(invalidMessage.value), 9, _hoisted_10))
        : _createCommentVNode("", true)
    ])
  ], 10, _hoisted_1))
}
}

})